import {
  type ChangeEvent,
  forwardRef,
  type HTMLProps,
  useEffect,
  useImperativeHandle,
} from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import clsx from "clsx";
import type { FieldProps } from "../Field";
import { Field } from "../Field";
import styles from "./styles.module.scss";

// Variants
export * from "./variants";

export type InputFieldProps = HTMLProps<HTMLInputElement> &
  FieldProps & {
    label: string;
    errorText?: string | false;
    helperText?: string;
    onChangeText?: (text: string) => void;
    onChangeNumber?: (number: number) => void;
    format?: (value: HTMLProps<HTMLInputElement>["value"]) => string;
    unformat?: (value: HTMLProps<HTMLInputElement>["value"]) => string;
    getNewCaretPosition?: (
      nextCaretPosition: number,
      currentCaretPosition: number,
      initialValue: string, // The original value of the input before the change
      newValue: string,
      newFormattedValue: string
    ) => number;
    isPopulated?: boolean;
    isMonospace?: boolean;
    className?: string;
    containerClassName?: string;
    disabled?: boolean;
  };

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      label,
      errorText,
      helperText,
      onChange,
      onChangeText,
      onChangeNumber,
      type,
      format,
      unformat,
      getNewCaretPosition,
      required,
      value = "",
      skeleton,
      isPopulated,
      isMonospace,
      className,
      containerClassName,
      onKeyDown,
      placeholder,
      disabled,
      hasInlineLabel = false,
      ...rest
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current);
    const caretPositionRef = useRef<number>(0);
    const [newCaretPosition, setNewCaretPosition] = useState<number | null>(
      null
    );

    const [internalValue, setInternalValue] = useState(
      format?.(value) ?? value
    );

    const internalIsPopulated = useMemo(
      () => isPopulated || !!internalValue || !!placeholder,
      [internalValue, isPopulated, placeholder]
    );
    const [block, cn] = useModuleBEM(styles, "InputField");

    const getFormattedValue = useCallback(
      (value: string) => format?.(value) ?? value,
      [format]
    );
    const getUnformattedValue = useCallback(
      (value: string) => unformat?.(getFormattedValue(value)) ?? value,
      [getFormattedValue, unformat]
    );

    const internalOnChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const formattedValue = getFormattedValue(value);
        const unformattedValue = getUnformattedValue(value);

        if (getNewCaretPosition) {
          const newCaretPosition = getNewCaretPosition(
            e.target.selectionStart,
            caretPositionRef.current,
            internalValue?.toString() ?? "",
            value,
            formattedValue
          );
          setNewCaretPosition(newCaretPosition);
        }

        onChange?.(e);
        onChangeText?.(unformattedValue ?? value);
        onChangeNumber?.(Number(unformattedValue ?? value));
        setInternalValue(formattedValue);
      },
      [
        onChange,
        onChangeText,
        onChangeNumber,
        getNewCaretPosition,
        getUnformattedValue,
        internalValue,
        getFormattedValue,
      ]
    );

    const onClickContainer = useCallback(() => {
      inputRef.current?.focus();
    }, []);

    useEffect(() => {
      if (newCaretPosition != null) {
        inputRef.current?.setSelectionRange?.(
          newCaretPosition,
          newCaretPosition
        );
        setNewCaretPosition(null);
        return;
      }
    }, [internalValue, newCaretPosition]);

    useEffect(() => {
      if (
        typeof value === "string" &&
        getFormattedValue(value) === internalValue
      ) {
        return;
      }
      setInternalValue(value);
    }, [value, getFormattedValue, internalValue]);

    return (
      <Field
        className={clsx(block, className)}
        containerClassName={containerClassName}
        onClickContainer={onClickContainer}
        errorText={errorText}
        helperText={helperText}
        label={label}
        isPopulated={internalIsPopulated}
        skeleton={skeleton}
        disabled={disabled}
        hasInlineLabel={hasInlineLabel}
      >
        <input
          value={internalValue}
          className={clsx(
            className,
            cn("input", {
              monospace: isMonospace,
              hasInlineLabel: hasInlineLabel,
            })
          )}
          onChange={internalOnChange}
          ref={inputRef}
          onKeyDown={(e) => {
            caretPositionRef.current = inputRef.current.selectionStart;
            onKeyDown?.(e);
          }}
          type={type}
          required={required}
          placeholder={placeholder}
          disabled={disabled}
          {...rest}
        />
      </Field>
    );
  }
);
